// @flow
import type { Saga } from "redux-saga";
import {
  call,
  put,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { change } from "redux-form";
import { Intent } from "@blueprintjs/core";

import { lookupTimezone } from "shared/utilities/hereTimezone";
import { makeApiCall } from "redux/middleware/api";
import { showToast } from "redux/modules/toasts";
import { loadInstructorLocations } from "redux/modules/businessEventScheduling/loadInstructorLocations";
import { toEventTimezone } from "./parsers/timezones";
import {
  INSTRUCTOR_LOCATIONS_REQUEST,
  POST_CABELAS_CLASSES_REQUEST,
  TIMEZONE_LOOKUP,
} from "./createCabelasClassActions";
import {
  receiveInstructorLocations,
  instructorLocationsError,
  postCabelasClassError,
  postCabelasClassSuccess,
  postCabelasClassesRowResult,
  requestTimezoneLookup,
  receiveTimezoneLookup,
  timezoneLookupError,
  clearTimezoneLookup,
} from "./createCabelasClassActionCreators";
import type {
  getInstructorLocationsAction,
  requestPostCabelasClassesAction,
  lookupLocationTimezoneAction,
} from "./createCabelasClassActionCreators";
import { EVENTS_API_ENDPOINTS } from "./constants";

type ApiErrorResponse = {
  errors?: Array<{ message?: string, detail?: string, status?: number }>,
  error?: {
    description?: string,
  },
};

const hasApiErrors = (json: mixed): boolean =>
  !!json &&
  typeof json === "object" &&
  (json.hasOwnProperty("errors") || json.hasOwnProperty("error"));

const getApiErrorMessage = (
  json: ApiErrorResponse,
  eventIndex: number
): string => {
  if (json.error?.description) {
    return json.error.description;
  }

  if (json.errors?.length) {
    return json.errors
      .map(
        (error: { message?: string, detail?: string }): string =>
          error.message || error.detail || "Unknown error"
      )
      .join(", ");
  }

  return `Event ${eventIndex + 1} could not be created`;
};

const getThrownErrorMessage = (error: mixed): string => {
  if (error instanceof Error) {
    return error.message;
  }

  return String(error || "Unable to create Cabela's classes");
};

const getPartialFailureMessage = (
  createdEventCount: number,
  totalEventCount: number,
  failureCount: number
): string =>
  `Created ${createdEventCount} of ${totalEventCount} classes. ${failureCount} ${
    failureCount === 1 ? "class" : "classes"
  } failed - open Batch results to see which and retry them.`;

const UNPROCESSABLE = 422; // 422s fail again on retry; timeouts have no status

const getErrorStatusCode = (json: ApiErrorResponse): ?number =>
  json.errors?.length ? json.errors[0].status : undefined;

export function* performPostCabelasClasses(
  action: requestPostCabelasClassesAction
): Saga<void> {
  const {
    payload: { events },
  } = action;

  let createdCount = 0;
  let failureCount = 0;

  for (let index = 0; index < events.length; index += 1) {
    const batchEvent = events[index];
    const batchIndex = batchEvent.batchIndex ?? index;
    const row = {
      batchIndex,
      sourceRowNumber: batchEvent.sourceRowNumber,
      sourceLabel: batchEvent.sourceLabel,
      startDate: batchEvent.event?.startDate,
    };

    try {
      const json = yield call(
        makeApiCall,
        "POST",
        EVENTS_API_ENDPOINTS.CREATE_EVENT,
        batchEvent.event,
        false
      );

      if (hasApiErrors(json)) {
        const statusCode = getErrorStatusCode(json);

        throw Object.assign(
          new Error(getApiErrorMessage(json, batchIndex)),
          { statusCode }
        );
      }

      createdCount += 1;
      yield put(
        postCabelasClassesRowResult({
          ...row,
          isCreated: true,
          isRetryable: false,
        })
      );
    } catch (error) {
      failureCount += 1;
      const statusCode = error?.statusCode;

      yield put(
        postCabelasClassesRowResult({
          ...row,
          isCreated: false,
          statusCode,
          isRetryable: statusCode !== UNPROCESSABLE,
          message: getThrownErrorMessage(error),
        })
      );
    }
  }

  if (!failureCount) {
    yield put(postCabelasClassSuccess({ data: [] }));
    yield put(
      showToast(
        `Submitted ${createdCount} Cabela's ${
          createdCount === 1 ? "class" : "classes"
        } for review`,
        Intent.SUCCESS
      )
    );
    return;
  }

  yield put(
    postCabelasClassError({
      errorMessage: getPartialFailureMessage(
        createdCount,
        events.length,
        failureCount
      ),
      errors: [],
    })
  );
  yield put(showToast("Couldn't Create Cabela's Classes", Intent.DANGER));
}

export function* watchPostCabelasClasses(): Saga<void> {
  yield takeEvery(POST_CABELAS_CLASSES_REQUEST, performPostCabelasClasses);
}

export function* performInstructorLocations({
  payload: { businessId },
}: getInstructorLocationsAction): Saga<void> {
  try {
    if (!businessId) {
      throw new Error("A business is required to load assigned locations.");
    }

    const locations = yield call(loadInstructorLocations, businessId);
    yield put(receiveInstructorLocations(locations));
  } catch (error) {
    yield put(
      instructorLocationsError({
        errorMessage:
          error?.message || "Unable to load assigned store locations.",
      })
    );
  }
}

export function* watchInstructorLocations(): Saga<void> {
  yield takeLatest(INSTRUCTOR_LOCATIONS_REQUEST, performInstructorLocations);
}

export function* performTimezoneLookup(
  action: lookupLocationTimezoneAction
): Saga<void> {
  const {
    payload: { location, form },
  } = action;

  yield put(change(form, "timezone", ""));

  if (!location) {
    yield put(clearTimezoneLookup());
    return;
  }

  yield put(requestTimezoneLookup());

  try {
    const hereTimezone = yield call(lookupTimezone, location.address);
    const timezone = toEventTimezone(hereTimezone.name);

    if (!timezone) {
      throw new Error(
        `The location uses ${hereTimezone.name}, which is not supported automatically. Choose the time zone manually.`
      );
    }

    yield put(change(form, "timezone", timezone));
    yield put(receiveTimezoneLookup(timezone));
  } catch (error) {
    yield put(
      timezoneLookupError({
        errorMessage:
          error?.message ||
          "Automatic time zone lookup failed. Choose the time zone manually.",
      })
    );
  }
}

export function* watchTimezoneLookup(): Saga<void> {
  yield takeLatest(TIMEZONE_LOOKUP, performTimezoneLookup);
}
